/**
 * Web's incoming-notification pipeline.
 *
 * Web parallel to apps/extension/src/background/index.ts's
 * `processIncomingMessage`. Trimmed to the minimum the foreground-only
 * MVP needs. No OS notification (foreground-only — the page is visible
 * to receive the SSE event in the first place; system-tray delivery is
 * the SW push path, see `sw/handlers.ts`). No call-filtering toggle
 * (the web settings panel is a follow-up).
 *
 * `processIncomingMessage` is an orchestrator: it decrypts and
 * dispatches, delegating all `notifications[]` storage mutation to
 * `services/notificationsStore.ts` (the sole owner of writes that
 * include the `notifications` key — grep-provable). Pipeline stages,
 * in order, inside one `try`/`catch`:
 *
 *   1. Snapshot read — one `webStorage.get` of every key the pipeline
 *      might touch.
 *   2. Not-paired guard — `!secretKey || !phonePubKey` → silent return
 *      (no seqId advance; there's nothing to decrypt with yet).
 *   3. Server-origin dispatch — `senderDeviceId === "server"` →
 *      `handleServerOriginMessage` (public-API envelopes, decrypted
 *      with `serverPublicKey` rather than the paired phone's key).
 *   4. Flat-list msgId dedup — a duplicate SSE event or /sync replay
 *      of an already-stored card short-circuits here. A CONTENT dedup
 *      follows the type dispatch (step 6), because `msgId` is a
 *      transport id the phone re-mints per relay and so cannot catch a
 *      re-relayed notification. (This note previously said content
 *      duplicates were "rare in practice" and deferred that pass to the
 *      extension. They are not rare — a listener rebind re-relays up to
 *      20 on-screen notifications with fresh ids — and that rationale is
 *      what kept the gap open.)
 *   5. Decrypt + `JSON.parse` -> `parsed`.
 *   6. Type dispatch, in this order (a new type's branch slots in
 *      anywhere before 6d — after it, every message of that type
 *      falls into the unknown-type warn):
 *        a. `"ping"`      -> `handlePing` (encrypted pong reply).
 *        b. `"clipboard"` -> guard -> `clipsStore.addClip` (bypasses
 *           `notifications[]` entirely) or warn.
 *        c. `"file_ctrl"` -> `dispatchIncomingFileCtrl` (bypasses
 *           `notifications[]` entirely, same as clipboard) or warn if the
 *           sender can't be resolved. Short-circuits BEFORE the unknown-type
 *           check below — it is a known type, not a fallthrough.
 *        d. neither of the above, and not `"notification"`/undefined
 *           -> unknown-type warn.
 *        e. `parsed.conversation` present -> `handleConversationMessage`
 *           (conversations module owns its own storage key + dedup).
 *        f. otherwise -> the flat append/update-merge path:
 *           `applyIncomingNotification` in `notificationsStore.ts`.
 *
 * Two invariants every branch (present and future) must uphold:
 *
 *   - **Invariant A**: every post-pair branch advances `lastSeqId` —
 *     either via the `advanceSeqId` helper or by folding `lastSeqId`
 *     into a batched `webStorage.set`. Skipping this stalls the
 *     cursor: the same message is re-fetched and re-processed on
 *     every subsequent /sync.
 *   - **Invariant B**: `processIncomingMessage` never throws. The
 *     whole body is one `try`/`catch` that logs and swallows — the
 *     SSE `notification` handler and the `/sync` catch-up loop both
 *     await this with no per-call try/catch of their own. A new
 *     branch's error handling must swallow-and-log internally,
 *     exactly like the ping/clipboard branches do.
 *
 * See `.claude/skills/web-add-incoming-message-type/SKILL.md` for the
 * full procedure for adding a new `type`.
 */

import {
  bumpCounter,
  type ConversationMessage,
  type DecryptedNotification,
  decideDisplay,
  type NotificationAction,
  type PublicSendEnvelope,
  parsePublicSendEnvelope,
  publicSendEnvelopeToNotification,
} from "@reflecto/client-core";
import { decryptAndParse, isSameNotificationContent } from "@reflecto/client-core/intake";
import {
  decodeBase64,
  decryptMessage,
  encodeBase64,
  encryptMessage,
  type PingPayload,
  type PongPayload,
  type SyncMessage,
} from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import { sendMessage } from "../api/client";
import { dispatchIncomingFileCtrl } from "../filetransfer/fileCtrl";
import { logError, logInfo, logWarn } from "../lib/log";
import { isClipboardTextPayload } from "./clipboard";
import { addClip } from "./clipsStore";
import { conversations } from "./conversationsStore";
import {
  APP_ICON_MAX_CHARS,
  applyIncomingNotification,
  applyServerOriginNotification,
} from "./notificationsStore";

/**
 * Defensive parser shape for the decrypted Android payload.
 *
 * Three flavours of "NotificationPayload" exist today:
 *   1. The strict canonical shape in `@reflecto/core/src/types.ts`
 *      (`NotificationPayload`) — required fields, schema-complete.
 *      Used by the server's typed dispatch path.
 *   2. The extension's intersection `NotificationPayload & { conversation: … }`
 *      in `apps/extension/src/background/index.ts` — adds the
 *      MessagingStyle wrapper that's only present on conversation
 *      notifications.
 *   3. The defensive parser type below — mirrors the extension's
 *      shape but loosens every field to optional so a malformed
 *      payload can't throw at parse time. The runtime checks
 *      (`parsed.type === "notification"`, etc.) restore the
 *      narrower contract before use.
 *
 * Unifying #1 + #3 into a shared "wire vs. defensive parser" pair in
 * `@reflecto/core` is a follow-up — server types touch this too and
 * deserve their own PR. Until then, **any new field on the canonical
 * server shape (e.g. `updateKey`, `tag`, `threadId`) must be added in
 * three places**: server, extension, and here.
 */
interface NotificationPayload {
  type?: "notification" | "ping" | "pong" | "clipboard" | "file_ctrl";
  app?: string;
  appPackage?: string;
  appIcon?: string;
  title?: string;
  body?: string;
  image?: string;
  timestamp?: number;
  category?: string;
  actions?: NotificationAction[];
  /** Android-side flag: this payload is an in-place update of an existing notification. */
  isUpdate?: boolean;
  /** Android tracker's prior msgId for the same sbnKey — used to find the existing card to merge into. */
  replacedMsgId?: string;
  conversation?: {
    key: string;
    sender: string | null;
    isGroup: boolean;
    groupName: string | null;
    messageHash: string;
  };
}

/**
 * Narrow `parsed` to a well-formed `PingPayload`. The caller is expected
 * to have already gated on `parsed.type === "ping"` — this predicate
 * only adds the `sentAt` narrowing. Canonical shape: `@reflecto/core`.
 */
function isPingPayload(parsed: NotificationPayload): parsed is NotificationPayload & PingPayload {
  return (
    parsed.type === "ping" && typeof (parsed as unknown as { sentAt?: unknown }).sentAt === "number"
  );
}

/**
 * Respond to a diagnostic ping with an encrypted pong. Reuses the
 * incoming msgId so Android can correlate the pong back to the
 * specific ping it sent. No queue collision: the ping sits in the
 * web's queue, the pong goes to Android's queue.
 *
 * Owns `lastSeqId` advancement (`finally` block) so the seqId is
 * always persisted regardless of how the pong attempt unwinds —
 * including a process death window between pong-send and the
 * caller's seqId write. Mirrors the extension's `handlePing`.
 */
async function handlePing(
  pingSentAt: number,
  msg: SyncMessage,
  phonePubKey: string,
  secretKey: string,
  token: string | undefined,
  androidDeviceId: string | undefined,
  lastSeqId: number | undefined
): Promise<void> {
  try {
    if (!token || !androidDeviceId) {
      logWarn("notifications", "ping_no_token");
      return;
    }
    const pong: PongPayload = {
      type: "pong",
      pingSentAt,
      receivedAt: Date.now(),
    };
    const wire = encryptMessage(
      new TextEncoder().encode(JSON.stringify(pong)),
      decodeBase64(phonePubKey),
      decodeBase64(secretKey)
    );
    await sendMessage(token, encodeBase64(wire), msg.msgId, androidDeviceId);
  } catch (err) {
    logError("notifications", "pong_send_failed", err);
  } finally {
    await advanceSeqId(msg.seqId, lastSeqId);
  }
}

/**
 * Process one incoming SyncMessage end-to-end. Idempotent on msgId for
 * regular notifications and on messageHash for conversation entries.
 * Errors are caught + logged; this never throws into the caller (SSE
 * loop relies on processing one event not killing the stream).
 */
export async function processIncomingMessage(msg: SyncMessage): Promise<void> {
  try {
    const {
      secretKey,
      phonePubKey,
      serverPublicKey,
      notifications,
      lastSeqId,
      appIconCache,
      unreadCount,
      token,
      androidDeviceId,
      hiddenApps,
      dndSchedule,
      dndSnoozeUntil,
    } = await webStorage.get(
      "secretKey",
      "phonePubKey",
      "serverPublicKey",
      "notifications",
      "lastSeqId",
      "appIconCache",
      "unreadCount",
      "token",
      "androidDeviceId",
      "hiddenApps",
      "dndSchedule",
      "dndSnoozeUntil"
    );
    if (!secretKey || !phonePubKey) return;

    // Public-API envelopes carry `senderDeviceId: "server"` and are encrypted
    // with the server keypair, not the paired phone's. Dispatch to a dedicated
    // handler that decrypts with `serverPublicKey` + renders through the same
    // notifications array. token_update envelopes share this channel; the
    // handler dispatches on `payload.type` and skips unknown variants.
    if (msg.senderDeviceId === "server") {
      await handleServerOriginMessage(msg, {
        notifications,
        lastSeqId,
        unreadCount,
        hiddenApps,
        dndSchedule,
        dndSnoozeUntil,
        secretKey,
        serverPublicKey,
      });
      return;
    }

    // msgId dedup for the flat `notifications` array (regular cards
    // re-delivered via /sync replay or duplicate SSE event).
    // Conversation messages bypass this gate — their dedup runs
    // separately inside `addConversationMessageIfNew` keyed on
    // `messageHash`. Both paths are guarded; the split exists
    // because conversation threads share a single thread per
    // `conversation.key` rather than one card per msgId.
    //
    // Update replay: a merged card retains the original `msgId` and stores
    // the update's id as `latestAndroidMsgId`. Match against both so a /sync
    // replay of an already-processed update doesn't re-fire the merge — which
    // would clobber any newer content a subsequent update had already applied.
    if (notifications?.some((n) => n.msgId === msg.msgId || n.latestAndroidMsgId === msg.msgId)) {
      // Already stored (duplicate SSE event or /sync replay). Advance the cursor anyway, like every
      // other early-return — else this seqId is re-fetched + re-deduped on every refresh (cursor stuck).
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }

    // Decrypt + parse WITHOUT letting a throw escape (the web half of #1453). Without this a
    // throw reached the outer catch below, which logs at error level and returns without
    // advancing `lastSeqId` -- so a poison entry at the head of the queue was re-fetched and
    // re-failed on every reconnect, with a Sentry event each time, until something newer
    // arrived.
    //
    // Slice 11h: these ten lines were a second copy of `client-core/intake`'s decrypt->parse,
    // and now they are that module. `decryptAndParse` rather than `classifyEnvelope` because
    // this path does its OWN family dispatch below (`ping` / `clipboard` / `file_ctrl` each
    // need the parsed object) and `classifyEnvelope` reports a non-notification by TYPE only,
    // discarding the value.
    //
    // ⚠️ `reason` now carries the shared taxonomy -- "decrypt" / "not_json" -- where it used to
    // carry the thrown error's `name` ("Error" / "SyntaxError"). Same two-way distinction, one
    // less way to leak: `JSON.parse` runs on DECRYPTED bytes and V8 quotes the offending
    // fragment into its SyntaxError message, which is notification plaintext, and the Sentry
    // scrubber matches known token/key patterns rather than arbitrary message bodies. Nothing
    // in the repo queried the old values. `detail` is deliberately NOT logged. WARN, not ERROR:
    // a lapsed key or a bad entry is a condition of the moment, not a defect, and error-level
    // ships to Sentry.
    const read = decryptAndParse(
      {
        decrypt: (payloadBase64) =>
          decryptMessage(
            decodeBase64(payloadBase64),
            decodeBase64(phonePubKey),
            decodeBase64(secretKey)
          ),
      },
      msg.payload
    );
    if (read.kind === "failed") {
      await advanceSeqId(msg.seqId, lastSeqId);
      logWarn("notifications", "sync_entry_unreadable", undefined, {
        msgId: msg.msgId,
        seqId: msg.seqId,
        reason: read.reason,
      });
      return;
    }
    // The cast is unchanged from before this slice, and deliberately so. `parsed` is really a
    // union -- the dispatch below narrows it by `type` -- and the notification branch defends
    // field by field (`parsed.appPackage ?? parsed.app?... ?? "unknown"`) rather than
    // validating. Introducing `isNotificationPayload` here would turn a payload that currently
    // renders with fallbacks into a silent drop: a behaviour change, not part of a move.
    const parsed = read.value as NotificationPayload;

    if (parsed.type === "ping") {
      // isPingPayload narrows `sentAt` to a number; the type discriminant
      // is already confirmed by the outer guard.
      if (isPingPayload(parsed)) {
        // handlePing advances `lastSeqId` itself in a `finally` block
        // so the seqId is persisted atomically with the pong attempt.
        await handlePing(
          parsed.sentAt,
          msg,
          phonePubKey,
          secretKey,
          token,
          androidDeviceId,
          lastSeqId
        );
      } else {
        logWarn("notifications", "ping_missing_sent_at");
        await advanceSeqId(msg.seqId, lastSeqId);
      }
      return;
    }

    // Clipboard clip from a paired device. Bypasses the notification
    // list state machine entirely — stored in the persisted clip-history
    // store (spec C1) and rendered in the headless `ClipboardScreen` (see
    // MainPage). NEVER auto-written to the OS clipboard; the user taps
    // the dock's copy button (the only point with DOM + user
    // activation). Clips bypass DND/decideDisplay (always shown in the
    // dock). Advance the cursor like every other branch so a /sync
    // replay doesn't loop. The clip content is never logged.
    //
    // Use `msg.msgId` as the entry `id` so a live SSE re-delivery dedups
    // via `createClipHistory`'s own dedup-by-id (it bumps the existing
    // entry to the front rather than inserting a duplicate row) — clips
    // never enter the `notifications[]` array, so the gate above is a
    // no-op for them. `peerDeviceId` (C4) is the sender: `senderDeviceId`
    // when present, else the paired phone (mirroring-path messages omit
    // it — same fallback `fileCtrl.ts` applies).
    if (parsed.type === "clipboard") {
      if (isClipboardTextPayload(parsed)) {
        // Awaited (not fire-and-forget): `createClipHistory.add()` never
        // rejects (Invariant B is safe), and awaiting keeps this branch's
        // completion deterministic for callers/tests instead of racing
        // `advanceSeqId` below on an unrelated async chain.
        await addClip({
          id: msg.msgId,
          direction: "received",
          content: parsed.content,
          peerLabel: parsed.origin.label,
          peerDeviceId: msg.senderDeviceId ?? androidDeviceId,
          peerSurface: parsed.origin.surface,
          provenance: parsed.provenance,
          sensitive: parsed.sensitive,
          timestamp: parsed.timestamp,
        });
      } else {
        logWarn("notifications", "clipboard_malformed");
      }
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }

    // File transfer control message (TASK-27 M4, docs/file-transfer-resume.md).
    // Bypasses the notification list entirely — routed to `fileCtrl.ts`'s
    // `dispatchIncomingFileCtrl`, which owns durable offer dedup + the
    // send/receive UI wiring (`useFileTransfers`). `msg.senderDeviceId` is
    // absent for mirroring-path messages (means "the paired phone" — same
    // fallback the extension's background/fileCtrl.ts applies).
    if (parsed.type === "file_ctrl") {
      const ctrlSenderDeviceId = msg.senderDeviceId ?? androidDeviceId;
      if (ctrlSenderDeviceId) {
        await dispatchIncomingFileCtrl(ctrlSenderDeviceId, parsed);
      } else {
        logWarn("notifications", "file_ctrl_no_sender");
      }
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }

    if (parsed.type !== "notification" && parsed.type !== undefined) {
      logWarn("notifications", "unknown_payload_type", undefined, {
        type: String(parsed.type),
      });
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }

    // Delivered counter (system class, §3.3) — before the conversation branch so
    // MessagingStyle messages count too. "Seen" is counted on visible render.
    void bumpCounter(webStorage, "notifications_delivered_count");

    if (parsed.conversation) {
      // Hand the already-loaded appIconCache down so the conversation
      // path doesn't issue a redundant `webStorage.get("appIconCache")`
      // round-trip. The outer `processIncomingMessage` runs the
      // single-message path serially (`for await` in sse.ts), so
      // there's no concurrent writer that could stale this snapshot
      // between read and use.
      const isNew = await handleConversationMessage(parsed, msg, appIconCache);

      // Per-device display gate, mirroring the extension's
      // `handleConversationMessage` (apps/extension/src/background/index.ts).
      // Conversation messages were already persisted via the
      // conversations module BEFORE this gate fires — that's
      // intentional. "hide" still means "don't bump the badge" but
      // the message must remain in the conversation thread so
      // multi-device read-state and history stay coherent.
      //
      // Resolve appPackage with the same fallback the regular path
      // uses below so the gate is keyed consistently across paths.
      const convAppPackage =
        parsed.appPackage ?? parsed.app?.toLowerCase().replace(/\s+/g, ".") ?? "unknown";
      const convDecision = decideDisplay(convAppPackage, {
        hiddenApps,
        dndSchedule,
        dndSnoozeUntil,
      });
      // "hide"/"silent" suppress the badge bump (persistence already happened in
      // handleConversationMessage; left intact for multi-device read-state). "show" bumps unreadCount —
      // but ONLY for a genuinely-new message (isNew); a /sync replay or duplicate of an already-seen
      // message must not re-count. Re-read unreadCount here (not the top-of-function snapshot) to close the
      // TOCTOU window vs markAllRead/dropOldest — see notifications.test.ts "rapid conversation
      // messages". Batch unreadCount + lastSeqId into one write; the cursor advances regardless of
      // isNew so a replay doesn't loop on the next /sync.
      const newSeqId = Math.max(msg.seqId, lastSeqId ?? 0);
      const seqIdChanged = newSeqId !== (lastSeqId ?? 0);
      const convUpdates: Parameters<typeof webStorage.set>[0] = {};
      if (isNew && convDecision === "show") {
        const { unreadCount: latest } = await webStorage.get("unreadCount");
        convUpdates.unreadCount = (latest ?? 0) + 1;
      }
      if (seqIdChanged) convUpdates.lastSeqId = newSeqId;
      if (Object.keys(convUpdates).length > 0) await webStorage.set(convUpdates);
      return;
    }

    // Regular (non-conversation) notification — append to flat list.
    const appPackage =
      parsed.appPackage ?? parsed.app?.toLowerCase().replace(/\s+/g, ".") ?? "unknown";

    // Resolved ONCE and shared with the `next` card below. Computing them twice is how the
    // dedup key and the stored card drift apart: an edit to one default silently stops the
    // other matching, and the symptom is duplicates reappearing with every test still green.
    const resolvedTitle = parsed.title ?? "";
    const resolvedBody = parsed.body ?? "";
    const resolvedTimestamp = parsed.timestamp ?? 0;

    // ⚠️ CONTENT dedup, and it is NOT redundant with the msgId dedup above. `msgId` is a
    // TRANSPORT id — the phone mints a fresh `UUID.randomUUID()` per relay — so it identifies
    // one delivery, never one notification. On every NotificationListenerService rebind (boot,
    // process death, app update, toggling notification access) the phone re-relays up to 20
    // still-on-screen notifications with FRESH ids, and the msgId gate cannot see any of them.
    // `timestamp` is the phone's `sbn.postTime`, unchanged for a notification that merely
    // lingered, which is what makes the content key match.
    //
    // Ported from `apps/extension/src/background/processing.ts`, which has carried this guard
    // — and a comment naming this exact scenario — while this file's header called content
    // duplicates "rare in practice" and deferred it. They are not rare; the extension was
    // right and this side was the outlier. Conversations are exempt on the phone
    // (`ActiveNotificationSynchronizer` reseeds `conversationTracker` before relaying, so
    // MessagingStyle notifications emit nothing on rebind), so this covers the rest: Gmail,
    // bank alerts, delivery updates.
    //
    // Only non-updates. An update is MEANT to replace an existing card, so deduping it would
    // suppress the merge. Scope is currently-stored notifications only: once a card is
    // dismissed, the same content may legitimately return as a new one.
    //
    // ORDER VS `decideDisplay` IS NOT SIGNIFICANT, and it deliberately differs from the
    // extension, which gates first and dedups second. Both orders give the same outcome: a
    // hidden app's notifications never reach `notifications[]`, so there is nothing here for
    // the dedup to match, and `decideDisplay` is pure — skipping it for a duplicate loses no
    // side effect. Dedup-first only avoids computing a decision that is about to be discarded.
    // Do not reorder to "match the extension" on the assumption that it matters.
    //
    // Accepted collision, same as the extension's: the same app posting identical title+body
    // in the same millisecond collapses to one card.
    //
    // ⚠️ NOT FIXED HERE: the SW push path. A rebind that lands while no tab is visible still
    // spawns a duplicate OS toast, because `sw/handlers.ts` anchors the tray `tag` to the
    // fresh msgId and has no dedup of its own. That is not an oversight to port over — the SW
    // deliberately has **no IDB read access on the push hot path** (it says so where it
    // explains the chained-update tag gap), so a content dedup there means crossing that
    // boundary, and the extension has no push path to copy from. Separate change.
    const incomingContent = {
      appPackage,
      title: resolvedTitle,
      body: resolvedBody,
      timestamp: resolvedTimestamp,
    };
    if (
      !parsed.isUpdate &&
      notifications?.some((n) => isSameNotificationContent(n, incomingContent))
    ) {
      // Advance like every other early return, or this seqId is re-fetched and re-deduped on
      // every refresh and the cursor sticks. unreadCount deliberately not bumped.
      //
      // Deliberately unlogged. A suppression here is expected behaviour, not a problem, and
      // this module's logging rule is that an un-actionable line is one nobody reads. It would
      // also carry `appPackage`, i.e. which apps this user mirrors. How OFTEN this fires is a
      // real open question — it is the rebind frequency nobody has measured — but a console
      // line is not the instrument for it.
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }

    // Per-device display gate (hidden apps + DND + Snooze 1h). Same
    // pure decision the SW push handler applies — see
    // `apps/web/src/sw/handlers.ts` for the OS-toast side. The
    // foreground path is responsible for the panel-storage side:
    //   "hide"   — drop the card; advance seqId so /sync replay won't
    //              re-process it.
    //   "silent" — write to the panel, but do not bump unreadCount
    //              (the badge is part of the toast UX the user opted
    //              to mute during quiet hours).
    //   "show"   — existing behaviour (panel append + unread bump).
    const displayDecision = decideDisplay(appPackage, {
      hiddenApps,
      dndSchedule,
      dndSnoozeUntil,
    });
    if (displayDecision === "hide") {
      await advanceSeqId(msg.seqId, lastSeqId);
      return;
    }
    const next: DecryptedNotification = {
      msgId: msg.msgId,
      seqId: msg.seqId,
      app: parsed.app ?? "Unknown",
      appPackage,
      // The same resolved values the content-dedup key above used — see the note there.
      title: resolvedTitle,
      body: resolvedBody,
      timestamp: resolvedTimestamp,
      receivedAt: Date.now(),
      actions: Array.isArray(parsed.actions) ? parsed.actions : undefined,
      // Mirror the extension's intake cap (background/index.ts): ~267K base64 chars
      // ≈ Android's 200KB WebP byteBudget (×4/3). Drop over-budget blobs before they
      // reach IndexedDB rather than persisting megabytes of unbounded image data.
      image:
        typeof parsed.image === "string" && parsed.image && parsed.image.length <= 270_000
          ? parsed.image
          : undefined,
      category: parsed.category,
      isUpdate: parsed.isUpdate,
      isRead: false,
    };

    // Update-merge, cap, write-once appIcon cache, and the batched storage
    // write all live in notificationsStore.ts (the sole owner of every
    // `notifications[]` write) — see `applyIncomingNotification`.
    await applyIncomingNotification({
      next,
      replacedMsgId: parsed.replacedMsgId,
      appIcon: parsed.appIcon,
      isSilent: displayDecision === "silent",
      notifications,
      unreadCount,
      appIconCache,
      lastSeqId,
    });
  } catch (err) {
    logError("notifications", "process_incoming_failed", err);
  }
}

/**
 * Append a conversation message via the shared conversations module. Mirrors the extension's
 * `handleConversationMessage` minus the OS notification side-effect. The conversations module owns
 * dedup, append, reverse-index update, and per-thread cap enforcement.
 *
 * Returns `isNew`: true if the message was newly appended, false if it was a dedup hit (already seen)
 * — the caller gates the unread bump on this so a /sync replay doesn't re-count.
 */
async function handleConversationMessage(
  content: NotificationPayload,
  msg: SyncMessage,
  appIconCache: Record<string, string> | undefined
): Promise<boolean> {
  // Type-narrow, not runtime validation: the caller gates on `parsed.conversation`, but TS doesn't
  // carry that narrowing onto the passed object, so the body needs this to access `content.conversation`.
  if (!content.conversation) return false;
  const conv = content.conversation;
  const convMessage: ConversationMessage = {
    id: conv.messageHash,
    msgId: msg.msgId,
    sender: conv.sender,
    text: content.body ?? "",
    timestamp: content.timestamp ?? 0,
    receivedAt: Date.now(),
    app: content.app ?? "Unknown",
    packageName: content.appPackage ?? "unknown",
  };
  const { isNew } = await conversations.addConversationMessageIfNew(conv.key, convMessage, {
    title: content.title ?? "",
    app: content.app ?? "Unknown",
    packageName: content.appPackage ?? "unknown",
    isGroup: conv.isGroup,
    groupName: conv.groupName,
    actions: Array.isArray(content.actions) ? content.actions : undefined,
  });

  // Cache the app icon if the payload carries one, it's new, and
  // it fits the size cap (mirrors the cap on the regular-notification
  // path above). `appIconCache` arrives pre-loaded from the caller —
  // no redundant get(). The set is still separate from
  // addConversationMessageIfNew's internal write because the
  // conversations module owns its own atomic write boundary; the
  // get-skip is the cheap optimisation, the set is unavoidable
  // without expanding the conversations API.
  if (content.appIcon && content.appIcon.length <= APP_ICON_MAX_CHARS) {
    const pkg = content.appPackage ?? "unknown";
    if (!appIconCache?.[pkg]) {
      await webStorage.set({
        appIconCache: { ...(appIconCache ?? {}), [pkg]: content.appIcon },
      });
    }
  }
  return isNew;
}

/**
 * Process a public-API send (`senderDeviceId === "server"`). Decrypts with the
 * stored server pubkey, parses the envelope, and routes by `payload.type`.
 * Falls back to a seqId-only advance on any failure so /v1/sync doesn't loop.
 */
async function handleServerOriginMessage(
  msg: SyncMessage,
  ctx: {
    notifications: DecryptedNotification[] | undefined;
    lastSeqId: number | undefined;
    unreadCount: number | undefined;
    hiddenApps: string[] | undefined;
    dndSchedule: Parameters<typeof decideDisplay>[1]["dndSchedule"];
    dndSnoozeUntil: number | undefined;
    secretKey: string;
    serverPublicKey: string | undefined;
  }
): Promise<void> {
  // MCP / public-API arrivals get their own rollup counter (event-spec §4.3).
  void bumpCounter(webStorage, "mcp_notifications_received_count");
  if (!ctx.serverPublicKey) {
    logWarn("notifications", "server_origin_no_pubkey");
    await advanceSeqId(msg.seqId, ctx.lastSeqId);
    return;
  }

  let envelope: PublicSendEnvelope | null = null;
  let plaintextStr: string | null = null;
  try {
    const wire = decodeBase64(msg.payload);
    const plaintext = decryptMessage(
      wire,
      decodeBase64(ctx.serverPublicKey),
      decodeBase64(ctx.secretKey)
    );
    plaintextStr = new TextDecoder().decode(plaintext);
    envelope = parsePublicSendEnvelope(plaintextStr);
  } catch (err) {
    // Name-only, matching the mirrored-path catch above -- for CONSISTENCY, not because this
    // site leaked. It cannot: every string `decryptMessage` throws is fixed text or a byte
    // count (decryption failed before any plaintext existed), and `parsePublicSendEnvelope`
    // catches its own JSON.parse and returns null. But the raw-object form is the pattern
    // #1453 retired, and whoever next adds a throw inside this try should not need to know
    // that history to stay safe.
    await advanceSeqId(msg.seqId, ctx.lastSeqId);
    logWarn("notifications", "server_origin_decrypt_failed", undefined, {
      reason: err instanceof Error ? err.name : "unknown",
    });
    return;
  }
  if (!envelope) {
    // Split tag so analytics can distinguish expected token_update churn from a
    // genuine parser regression. token_update is broadcast on every token
    // create/rotate/delete.
    const tag = peekIsTokenUpdate(plaintextStr)
      ? "server_origin_token_update"
      : "server_origin_unknown_envelope";
    logInfo("notifications", tag);
    await advanceSeqId(msg.seqId, ctx.lastSeqId);
    return;
  }

  if (ctx.notifications?.some((n) => n.msgId === msg.msgId)) {
    logWarn("notifications", "server_origin_msgid_dedup", undefined, { msgId: msg.msgId });
    await advanceSeqId(msg.seqId, ctx.lastSeqId);
    return;
  }

  const notification = publicSendEnvelopeToNotification(envelope, msg.msgId, msg.seqId);

  const displayDecision = decideDisplay(notification.appPackage, {
    hiddenApps: ctx.hiddenApps,
    dndSchedule: ctx.dndSchedule,
    dndSnoozeUntil: ctx.dndSnoozeUntil,
  });
  if (displayDecision === "hide") {
    logWarn("notifications", "server_origin_display_hide", undefined, {
      appPackage: notification.appPackage,
    });
    await advanceSeqId(msg.seqId, ctx.lastSeqId);
    return;
  }

  // Prepend + cap + unread math + the batched storage write all live in
  // notificationsStore.ts (the sole owner of every `notifications[]`
  // write) — see `applyServerOriginNotification`. No update-merge on this
  // path: every public-API send is a fresh card.
  const { listSize } = await applyServerOriginNotification({
    notification,
    isSilent: displayDecision === "silent",
    notifications: ctx.notifications,
    unreadCount: ctx.unreadCount,
    lastSeqId: ctx.lastSeqId,
    incomingSeqId: msg.seqId,
  });
  logInfo("notifications", "server_origin_local_add", {
    msgId: msg.msgId,
    seqId: msg.seqId,
    listSize,
    decision: displayDecision,
  });
}

async function advanceSeqId(incoming: number, lastSeqId: number | undefined): Promise<void> {
  const next = Math.max(incoming, lastSeqId ?? 0);
  if (next !== (lastSeqId ?? 0)) {
    await webStorage.set({ lastSeqId: next });
  }
}

function peekIsTokenUpdate(plaintext: string | null): boolean {
  if (!plaintext) return false;
  try {
    const raw = JSON.parse(plaintext) as { type?: unknown };
    return raw?.type === "token_update";
  } catch {
    return false;
  }
}
